// Recorded-audio playback for song attachments.
//
// The Transport remains the source of truth for beat position. AudioTrack is a
// follower: callers explicitly load, play, pause, seek, and update tempo. This
// class keeps the media element aligned to the transport with a small drift
// controller and protects async fades/loads from racing after dispose/reload.

const ONSET_THRESHOLD = 0.05;
const SILENCE_THRESHOLD = 0.02;
const SILENCE_HOLD_MS = 200;
const ENVELOPE_TAU_MS = 8;

const SEEK_FADE_SEC = 0.012;
const DRIFT_POLL_MS = 500;
const DRIFT_NONE_SEC = 0.01;
const SMALL_DRIFT_SEC = 0.15;
const RATE_NUDGE_HOLD_MS = 400;
const RATE_NUDGE_MAX = 0.03;
const DEFAULT_DURATION_EPSILON_SEC = 0.01;

type DriftAction = 'none' | 'rate-nudge' | 'hard-seek';
type DriftStepResult = DriftAction | 'paused';
type TrackState = 'idle' | 'loading' | 'ready' | 'disposed';

export function findFirstOnset(samples: Float32Array, sampleRate: number): number {
  if (samples.length === 0 || sampleRate <= 0) return 0;

  const frameSize = 256;
  const frameMs = (frameSize / sampleRate) * 1000;
  const alpha = 1 - Math.exp(-frameMs / ENVELOPE_TAU_MS);
  const silentFramesNeeded = Math.ceil(SILENCE_HOLD_MS / frameMs);
  let silentFrames = silentFramesNeeded;
  let env = 0;
  let prevEnv = 0;

  for (let start = 0; start < samples.length; start += frameSize) {
    const end = Math.min(start + frameSize, samples.length);
    let peak = 0;
    for (let i = start; i < end; i++) {
      const abs = Math.abs(samples[i]);
      if (abs > peak) peak = abs;
    }

    prevEnv = env;
    env += alpha * (peak - env);

    if (env < SILENCE_THRESHOLD) {
      silentFrames++;
      continue;
    }

    if (silentFrames >= silentFramesNeeded && prevEnv <= ONSET_THRESHOLD && env > ONSET_THRESHOLD) {
      return start / sampleRate;
    }
    silentFrames = 0;
  }

  return 0;
}

/**
 * Decode the opening section of an audio file and estimate the first useful
 * onset. Browser-only; returns 0 in non-WebAudio test environments.
 */
export async function detectBeatOneFromArrayBuffer(buf: ArrayBuffer): Promise<number> {
  if (typeof OfflineAudioContext === 'undefined') return 0;

  const targetSec = 15;
  const sampleRate = 44100;
  const ctx = new OfflineAudioContext(1, Math.floor(targetSec * sampleRate), sampleRate);

  let decoded: AudioBuffer;
  try {
    decoded = await ctx.decodeAudioData(buf.slice(0));
  } catch {
    return 0;
  }

  const len = Math.min(decoded.length, Math.floor(targetSec * decoded.sampleRate));
  if (len <= 0 || decoded.numberOfChannels <= 0) return 0;

  const mono = new Float32Array(len);
  for (let ch = 0; ch < decoded.numberOfChannels; ch++) {
    const data = decoded.getChannelData(ch);
    for (let i = 0; i < len; i++) mono[i] += data[i] / decoded.numberOfChannels;
  }

  return findFirstOnset(mono, decoded.sampleRate);
}

export interface AudioTrackOpts {
  audioContext: AudioContext;
  onEnded?: () => void;
  onError?: (message: string) => void;
  /** Test-only: inject a stand-in for the HTMLAudioElement. */
  audioElForTests?: HTMLAudioElement;
}

export class AudioTrack {
  private readonly audioEl: HTMLAudioElement;
  private readonly ctx: AudioContext;
  private readonly onEnded?: () => void;
  private readonly onError?: (message: string) => void;
  private readonly endedHandler: () => void;
  private readonly errorHandler: () => void;

  private source: MediaElementAudioSourceNode | null = null;
  private readonly gain: GainNode;
  private state: TrackState = 'idle';
  private generation = 0;
  private muted = false;
  private driftTimer: ReturnType<typeof setInterval> | null = null;

  private expectedStartSec = 0;
  private expectedStartedAt = 0;
  private expectationsActive = true;
  private tempoScale = 1;
  private rateHoldUntil = 0;

  constructor(opts: AudioTrackOpts) {
    this.ctx = opts.audioContext;
    this.onEnded = opts.onEnded;
    this.onError = opts.onError;
    this.audioEl = opts.audioElForTests ?? new Audio();
    this.audioEl.crossOrigin = 'anonymous';
    this.audioEl.preload = 'auto';
    this.setPitchPreservation(true);

    this.gain = this.ctx.createGain();
    this.gain.gain.value = 1;
    this.gain.connect(this.ctx.destination);

    this.endedHandler = () => this.onEnded?.();
    this.errorHandler = () => {
      const err = this.audioEl.error;
      this.onError?.(err ? `audio error ${err.code}` : 'audio error');
    };
    this.audioEl.addEventListener('ended', this.endedHandler);
    this.audioEl.addEventListener('error', this.errorHandler);
  }

  async load(url: string): Promise<{ durationSec: number }> {
    this.assertUsable();
    const loadGeneration = ++this.generation;
    this.state = 'loading';
    this.stopDriftWatch();
    this.expectationsActive = false;
    this.rateHoldUntil = 0;
    this.audioEl.pause();
    this.audioEl.src = url;

    try {
      this.audioEl.load();
    } catch {
      // Some test doubles implement load as a no-op or omit native loading.
    }

    await this.waitForMetadata(loadGeneration);
    this.assertCurrent(loadGeneration);
    this.ensureSourceNode();
    this.state = 'ready';
    return { durationSec: this.safeDuration() };
  }

  isLoaded(): boolean {
    return this.state === 'ready' && this.source !== null;
  }

  mute(value: boolean): void {
    this.muted = value;
    this.gain.gain.cancelScheduledValues(this.ctx.currentTime);
    this.gain.gain.value = value ? 0 : 1;
  }

  isMuted(): boolean {
    return this.muted;
  }

  async seekToBeat(beat: number, bpm: number, offsetSec: number): Promise<void> {
    this.assertReady();
    const targetSec = this.clampMediaTime(this.beatToAudioTime(beat, bpm, offsetSec));
    await this.fadeAround(() => {
      this.audioEl.currentTime = targetSec;
    });
    if (!this.audioEl.paused) this.anchorExpectations(performance.now(), targetSec);
  }

  async play(beat: number, bpm: number, offsetSec: number, tempoScale: number): Promise<void> {
    this.assertReady();
    this.setTempoScale(tempoScale);
    await this.seekToBeat(beat, bpm, offsetSec);
    this.anchorExpectations(performance.now(), this.audioEl.currentTime);

    try {
      await this.audioEl.play();
    } catch (err) {
      this.expectationsActive = false;
      this.stopDriftWatch();
      throw err;
    }

    this.startDriftWatch();
  }

  pause(): void {
    this.audioEl.pause();
    this.expectationsActive = false;
    this.stopDriftWatch();
  }

  setTempoScale(scale: number): void {
    const nextScale = Number.isFinite(scale) && scale > 0 ? scale : 1;
    if (!this.audioEl.paused && this.expectationsActive) {
      this.anchorExpectations(performance.now(), this.audioEl.currentTime);
    }
    this.tempoScale = nextScale;
    this.audioEl.playbackRate = nextScale;
    this.rateHoldUntil = 0;
  }

  dispose(): void {
    this.generation++;
    this.state = 'disposed';
    this.stopDriftWatch();
    this.expectationsActive = false;
    this.audioEl.removeEventListener('ended', this.endedHandler);
    this.audioEl.removeEventListener('error', this.errorHandler);

    try {
      this.audioEl.pause();
      this.audioEl.removeAttribute('src');
      this.audioEl.load();
    } catch {
      // Ignore browser/media cleanup failures during teardown.
    }

    try {
      this.source?.disconnect();
      this.gain.disconnect();
    } catch {
      // Disconnect can throw on already-disconnected Web Audio nodes.
    }
    this.source = null;
  }

  /** Exposed for tests: classify a drift value without touching the media element. */
  classifyDrift(driftSec: number): DriftAction {
    const abs = Math.abs(driftSec);
    if (abs <= DRIFT_NONE_SEC) return 'none';
    if (abs < SMALL_DRIFT_SEC) return 'rate-nudge';
    return 'hard-seek';
  }

  /**
   * Exposed for tests: run one drift-watch step against a supplied clock and
   * measured media position.
   */
  _driftStepForTests(nowMs: number, actualSec: number): DriftStepResult {
    if (this.audioEl.paused) return 'paused';
    if (!this.expectationsActive) this.anchorExpectations(nowMs, actualSec);

    const expected = this.expectedAt(nowMs);
    const drift = actualSec - expected;
    const mode = this.classifyDrift(drift);

    if (mode === 'hard-seek') {
      const generation = this.generation;
      void this.fadeAround(() => {
        if (this.generation !== generation || this.state === 'disposed') return;
        this.audioEl.currentTime = this.clampMediaTime(expected);
      });
      this.anchorExpectations(nowMs, expected);
      this.audioEl.playbackRate = this.tempoScale;
      this.rateHoldUntil = 0;
      return mode;
    }

    if (mode === 'rate-nudge') {
      this.applyRateNudge(drift);
      return mode;
    }

    if (nowMs >= this.rateHoldUntil && this.audioEl.playbackRate !== this.tempoScale) {
      this.audioEl.playbackRate = this.tempoScale;
    }
    return mode;
  }

  private assertUsable(): void {
    if (this.state === 'disposed') throw new Error('AudioTrack has been disposed');
  }

  private assertReady(): void {
    this.assertUsable();
    if (!this.isLoaded()) throw new Error('AudioTrack is not loaded');
  }

  private assertCurrent(expectedGeneration: number): void {
    this.assertUsable();
    if (this.generation !== expectedGeneration) throw new Error('stale audio load');
  }

  private waitForMetadata(loadGeneration: number): Promise<void> {
    if (this.audioEl.readyState >= HTMLMediaElement.HAVE_METADATA) return Promise.resolve();

    return new Promise<void>((resolve, reject) => {
      let done = false;
      const cleanup = (): void => {
        this.audioEl.removeEventListener('loadedmetadata', onMeta);
        this.audioEl.removeEventListener('durationchange', onMeta);
        this.audioEl.removeEventListener('error', onErr);
      };
      const finish = (fn: () => void): void => {
        if (done) return;
        done = true;
        cleanup();
        fn();
      };
      const onMeta = (): void => {
        finish(() => {
          try {
            this.assertCurrent(loadGeneration);
            resolve();
          } catch (err) {
            reject(err);
          }
        });
      };
      const onErr = (): void => {
        finish(() => reject(new Error('audio load failed')));
      };

      this.audioEl.addEventListener('loadedmetadata', onMeta);
      this.audioEl.addEventListener('durationchange', onMeta);
      this.audioEl.addEventListener('error', onErr);
    });
  }

  private ensureSourceNode(): void {
    if (this.source) return;
    this.source = this.ctx.createMediaElementSource(this.audioEl);
    this.source.connect(this.gain);
  }

  private safeDuration(): number {
    return Number.isFinite(this.audioEl.duration) && this.audioEl.duration > 0 ? this.audioEl.duration : 0;
  }

  private beatToAudioTime(beat: number, bpm: number, offsetSec: number): number {
    const safeBeat = Number.isFinite(beat) ? beat : 0;
    const safeBpm = Number.isFinite(bpm) && bpm > 0 ? bpm : 120;
    const safeOffset = Number.isFinite(offsetSec) ? offsetSec : 0;
    return safeOffset + (safeBeat * 60) / safeBpm;
  }

  private clampMediaTime(timeSec: number): number {
    const safeTime = Number.isFinite(timeSec) ? timeSec : 0;
    const duration = this.safeDuration();
    if (duration <= 0) return Math.max(0, safeTime);
    return Math.max(0, Math.min(duration - DEFAULT_DURATION_EPSILON_SEC, safeTime));
  }

  private anchorExpectations(nowMs: number, mediaSec: number): void {
    this.expectedStartedAt = nowMs;
    this.expectedStartSec = mediaSec;
    this.expectationsActive = true;
  }

  private expectedAt(nowMs: number): number {
    const elapsedSec = ((nowMs - this.expectedStartedAt) / 1000) * this.tempoScale;
    return this.expectedStartSec + elapsedSec;
  }

  private async fadeAround(mutator: () => void): Promise<void> {
    const generation = this.generation;
    const now = this.ctx.currentTime;
    const target = this.muted ? 0 : 1;
    const currentGain = this.gain.gain.value;

    this.gain.gain.cancelScheduledValues(now);
    this.gain.gain.setValueAtTime(currentGain, now);
    this.gain.gain.linearRampToValueAtTime(0, now + SEEK_FADE_SEC);

    await this.waitForAudioTime(now + SEEK_FADE_SEC);
    if (this.generation !== generation || this.state === 'disposed') return;

    mutator();

    const after = this.ctx.currentTime;
    this.gain.gain.setValueAtTime(0, after);
    this.gain.gain.linearRampToValueAtTime(target, after + SEEK_FADE_SEC);
  }

  private waitForAudioTime(whenSec: number): Promise<void> {
    return new Promise<void>((resolve) => {
      let settled = false;
      const finish = (): void => {
        if (settled) return;
        settled = true;
        resolve();
      };

      try {
        const src = this.ctx.createConstantSource();
        src.onended = finish;
        src.start(this.ctx.currentTime);
        src.stop(Math.max(this.ctx.currentTime, whenSec));
      } catch {
        setTimeout(finish, Math.max(1, Math.ceil((whenSec - this.ctx.currentTime) * 1000) + 2));
      }

      setTimeout(finish, Math.max(1, Math.ceil((whenSec - this.ctx.currentTime) * 1000) + 30));
    });
  }

  private applyRateNudge(driftSec: number): void {
    const factor = 1 - (0.6 * driftSec) / SMALL_DRIFT_SEC;
    const clamped = Math.max(1 - RATE_NUDGE_MAX, Math.min(1 + RATE_NUDGE_MAX, factor));
    this.audioEl.playbackRate = this.tempoScale * clamped;
    this.rateHoldUntil = performance.now() + RATE_NUDGE_HOLD_MS;
  }

  private startDriftWatch(): void {
    this.stopDriftWatch();
    this.driftTimer = setInterval(() => {
      this._driftStepForTests(performance.now(), this.audioEl.currentTime);
    }, DRIFT_POLL_MS);
  }

  private stopDriftWatch(): void {
    if (this.driftTimer !== null) {
      clearInterval(this.driftTimer);
      this.driftTimer = null;
    }
  }

  private setPitchPreservation(value: boolean): void {
    const el = this.audioEl as HTMLMediaElement & {
      preservesPitch?: boolean;
      mozPreservesPitch?: boolean;
      webkitPreservesPitch?: boolean;
    };
    el.preservesPitch = value;
    el.mozPreservesPitch = value;
    el.webkitPreservesPitch = value;
  }
}
